import React, { useState, useEffect } from 'react';
import { type TagColor, SimpleTag as Tag } from '@atlaskit/tag';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import {
	getLimit,
	LIMIT_DEFAULTS,
	MAX_SLA_GOALS_PER_SD,
} from '@atlassian/jira-servicedesk-common/src/ui/components/limit-data';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { useGoalCounter } from '../../../controllers/goal-counter';
import messages from './messages';

type Props = Intl;

export const GoalCounter = ({ intl: { formatMessage, formatNumber } }: Props) => {
	const [{ goalsCount: allGoals }, actions] = useGoalCounter();
	const safeLimitForNumberOfGoals = 0.75;

	const [maxGoalsPerSD, setMaxGoalsPerSD] = useState<number>(LIMIT_DEFAULTS[MAX_SLA_GOALS_PER_SD]);

	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		async function fetchMaxGoalsPerSD() {
			setIsLoading(true);
			await getLimit(MAX_SLA_GOALS_PER_SD)
				.then((limit) => {
					setMaxGoalsPerSD(limit || LIMIT_DEFAULTS[MAX_SLA_GOALS_PER_SD]);
				})
				.catch(() => {
					setMaxGoalsPerSD(LIMIT_DEFAULTS[MAX_SLA_GOALS_PER_SD]);
				});
			setIsLoading(false);
		}
		fetchMaxGoalsPerSD();
	}, []);

	const SAFE_LIMIT_FOR_NUMBER_OF_GOALS = Math.floor(safeLimitForNumberOfGoals * maxGoalsPerSD);

	const textForGoalCounter = `${formatNumber(allGoals)} ${formatMessage(
		messages.goalCounterTextDesc,
	)} ${formatNumber(maxGoalsPerSD)}`;

	let goalCounterColor: TagColor = 'blueLight';

	if (allGoals < SAFE_LIMIT_FOR_NUMBER_OF_GOALS) {
		goalCounterColor = 'blueLight';
		actions.setYellowFlagGoalCounter(false);
		actions.setRedFlagForGoalCount(false);
	} else if (allGoals >= SAFE_LIMIT_FOR_NUMBER_OF_GOALS && allGoals < maxGoalsPerSD) {
		goalCounterColor = 'yellowLight';
		actions.setYellowFlagGoalCounter(true);
		actions.setRedFlagForGoalCount(false);
	} else {
		goalCounterColor = 'redLight';
		actions.setYellowFlagGoalCounter(false);
		actions.setRedFlagForGoalCount(true);
	}

	return (
		<>
			{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
			<h5>
				{formatMessage(messages.goalCounterText)}
				{!isLoading && <Tag text={textForGoalCounter} color={goalCounterColor} />}
			</h5>
		</>
	);
};

export const Goal = injectIntl(GoalCounter);
